import styled, { useTheme } from 'styled-components'
import ContentLoader from 'react-content-loader'
import { ReactNode } from 'react'

import { IconInfo } from 'components/Icons/InfoIcon'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Typography } from 'components/Typography/Typography'
import { useIsMobile } from 'utils/useIsMobile'

export interface CurrentMetrics {
  value: string
  name: string
  tooltipText: ReactNode
  pctDiff?: string
  isValueNegative?: boolean
}

interface Props {
  metricsValues: CurrentMetrics[]
  isLoading: boolean
  style?: React.CSSProperties
}

const MetricsOverviewContainer = styled.div`
  padding: 24px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    padding: 12px;
  }
`

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 64px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    gap: 32px;
    flex-wrap: wrap;
  }
`

const PositionBreakdownTitle = styled(Typography)`
  margin-top: 8px;
  display: flex;
  align-items: center;
  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      width: 16px;
      height: 16px;
      & > path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

export const ColumnContentLoading = () => {
  const theme = useTheme()

  return (
    <ContentLoader
      speed={2}
      width={130}
      height={25}
      viewBox="0 0 130 25"
      backgroundColor={theme.colors.contentLoader.backgroundColor}
      foregroundColor={theme.colors.contentLoader.foregroundColor}
    >
      <rect x="0" y="8" rx="3" ry="3" width="75" height="7" />
      <rect x="0" y="26" rx="3" ry="3" width="130" height="7" />
    </ContentLoader>
  )
}

export const MetricsOverview = ({ metricsValues, isLoading, style }: Props) => {
  const isMobile = useIsMobile()

  if (isLoading) {
    return (
      <MetricsOverviewContainer style={style}>
        <Container>
          {[1, 2, 3, 4, 5].map(currentMetric => (
            <div key={currentMetric}>
              <ColumnContentLoading />
              <ColumnContentLoading />
            </div>
          ))}
        </Container>
      </MetricsOverviewContainer>
    )
  }

  return (
    <MetricsOverviewContainer style={style}>
      <Container>
        {metricsValues.map(currentMetric => (
          <div key={currentMetric.name}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
              <Typography fontWeight="bold" variant={isMobile ? 'body' : 'label1'}>
                {currentMetric.value}
              </Typography>
              {typeof currentMetric.pctDiff !== 'undefined' && (
                <Typography variant="body" color={currentMetric.isValueNegative ? 'danger' : 'success'}>
                  {currentMetric.pctDiff}
                </Typography>
              )}
            </div>
            <PositionBreakdownTitle variant="body" color="secondary">
              {currentMetric.name}
              <Tooltip
                content={currentMetric.tooltipText}
                position="top"
                tooltipBoxStyles={{ minWidth: isMobile ? 250 : 350, marginLeft: isMobile ? 60 : 100 }}
              >
                <IconInfo width={16} height={16} />
              </Tooltip>
            </PositionBreakdownTitle>
          </div>
        ))}
      </Container>
    </MetricsOverviewContainer>
  )
}
